import i18n from 'i18next'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import api from 'src/api'
import {
  LightPolicy,
  Policy_StatusEnum,
  PolicyAggregation,
  DomainPermission_VerbEnum,
  DomainPermission_DomainEnum,
  Favorite_PageIdEnum,
} from 'src/api/gen/api'
import { FEE_RATES_POLICY_KINDS } from 'src/commons/constants'
import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import { useURLAndStorageState } from 'src/commons/hooks/useURLAndStorageState'
import { userHasPermission } from 'src/commons/permissions'
import { IPoliciesListFilters, filters_to_request_params } from 'src/commons/policies/policies'
import { getJurisdictionTimezone } from 'src/commons/temporalHelpers'
import { useProviderUser } from 'src/layout/hooks'
import { FilteringCard } from 'src/muiv5/components/commons/Cards/FilteringCard'

import FeeRatesFilterPanel from './FilterPanel'
import FeeRatesTable from './Table'

export const getInitialFilters = (isProviderUser: boolean): IPoliciesListFilters => {
  const status: Policy_StatusEnum[] = [Policy_StatusEnum.upcoming, Policy_StatusEnum.ongoing]
  if (!isProviderUser) {
    status.push(Policy_StatusEnum.draft)
  }
  return {
    name: '',
    status: status,
    kind: [...FEE_RATES_POLICY_KINDS],
    providers: [],
  }
}

export const usePoliciesList = (filters: IPoliciesListFilters, defaultFilters: IPoliciesListFilters) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [policies, setPolicies] = useState<LightPolicy[]>([])
  const [policiesTotalCount, setPoliciesTotalCount] = useState(0)
  const [policiesAggregation, setPoliciesAggregation] = useState<PolicyAggregation>({
    kinds: {},
    statuses: {},
    providers: {},
    total: 0,
  })

  const fetchDataPolicies = async (signal?: AbortSignal) => {
    setLoading(true)
    try {
      const response = await api.policies.policiesListLight({
        ...filters_to_request_params(filters, defaultFilters),
        options: { signal },
      })
      setPolicies(response.results)
      setPoliciesTotalCount(response.aggregation.total)
      setPoliciesAggregation(response.aggregation)
    } catch (exception) {
      if (!signal?.aborted) {
        console.error(exception)
      }
    }
    setLoading(false)
  }

  return { policies, policiesTotalCount, policiesAggregation, fetchDataPolicies, loading }
}

const FeeRatesList = (props: RouteComponentProps) => {
  const { t } = useTranslation()
  const timezone = getJurisdictionTimezone()
  const isProviderUser = useProviderUser()
  const defaultFilters = useMemo(() => getInitialFilters(isProviderUser), [isProviderUser])
  const [filters, setFilters] = useURLAndStorageState(Favorite_PageIdEnum.fee_rates_list, defaultFilters)

  const displayedFiltersRef = useRef<IPoliciesListFilters>()

  const { policies, policiesTotalCount, policiesAggregation, loading, fetchDataPolicies } = usePoliciesList(
    filters,
    defaultFilters
  )

  async function fetchExportData() {
    await api.policies.policiesExport({
      // Export dates in the timezone and language of the user
      tz: timezone,
      lang: i18n.language,
      ...filters_to_request_params(filters, defaultFilters),
    })
  }

  function onApplyFilterClick() {
    if (!displayedFiltersRef.current) {
      throw new Error('There are no displayed filters')
    }

    setFilters(displayedFiltersRef.current)
  }

  const onClickExport = function (event: React.MouseEvent<HTMLElement>) {
    event.preventDefault()
    fetchExportData()
  }

  const onPolicyNameChange = (value: string) => {
    setFilters((pendingFilters) => ({ ...pendingFilters, name: value }))
  }

  useDeepCompareEffect(() => {
    const abortController = new AbortController()
    fetchDataPolicies(abortController.signal)
    return () => abortController.abort()
  }, [filters])

  return (
    <FilteringCard
      SideComponent={
        <FeeRatesFilterPanel
          ref={displayedFiltersRef}
          initialFilters={filters}
          defaultFilters={defaultFilters}
          policiesAggregation={policiesAggregation}
          policiesCount={policiesTotalCount}
          onNewPolicyClick={() => props.history.push('/fee_rates/create')}
        />
      }
      onApplyFilter={onApplyFilterClick}
    >
      <FeeRatesTable
        data={policies}
        loading={loading}
        totalCount={policiesTotalCount}
        onClickExport={
          userHasPermission([DomainPermission_VerbEnum.analyze, DomainPermission_DomainEnum.fee_rates])
            ? onClickExport
            : undefined
        }
        searchBar={{
          value: filters.name,
          onChange: onPolicyNameChange,
          placeholder: t('placeholders.fee_rates'),
        }}
      />
    </FilteringCard>
  )
}

export default withRouter(FeeRatesList)
